/* eslint-disable @typescript-eslint/no-explicit-any */
import { Canvas } from 'fabric';

import { TextEntity } from '../../metaBullets/classes/TextEntity';

import { isTextEntityObject } from './markerUtils';

/**
 * Получает все текстовые сущности метабулита
 */
const getMetaBulletEntities = (canvas: Canvas, metaBulletId: string): TextEntity[] => {
  const allObjects = canvas.getObjects();
  return allObjects.filter(
    (obj): obj is TextEntity =>
      isTextEntityObject(obj) && (obj as any).metaBulletId === metaBulletId,
  );
};

/**
 * Сортирует текстовые сущности по их позиции сверху вниз
 */
const sortEntitiesByPosition = (entities: TextEntity[]): TextEntity[] => {
  return entities.sort((a, b) => {
    const topA = a.top || 0;
    const topB = b.top || 0;
    return topA - topB;
  });
};

/**
 * Находит все метабулиты на канвасе и сортирует их по позиции
 */
const getAllMetaBulletsIdsInOrder = (canvas: Canvas): string[] => {
  const allObjects = canvas.getObjects();
  const metaBulletIds = new Set<string>();

  allObjects.forEach((obj) => {
    if (isTextEntityObject(obj) && (obj as any).metaBulletId) {
      metaBulletIds.add((obj as any).metaBulletId);
    }
  });

  // Получаем первую текстовую сущность каждого метабулита для определения позиции
  const metaBulletsWithPositions = Array.from(metaBulletIds).map((id) => {
    const entities = getMetaBulletEntities(canvas, id);
    const sortedEntities = sortEntitiesByPosition(entities);
    const firstEntity = sortedEntities[0];
    return {
      id,
      top: firstEntity?.top || 0,
    };
  });

  // Сортируем метабулиты по их позиции сверху вниз
  return metaBulletsWithPositions.sort((a, b) => a.top - b.top).map((mb) => mb.id);
};

/**
 * Навигация между текстовыми сущностями в метабулите
 */
export const navigateToTextEntity = (
  canvas: Canvas,
  currentEntity: TextEntity,
  direction: 'up' | 'down' | 'left' | 'right',
): boolean => {
  const metaBulletId = (currentEntity as any).metaBulletId;
  if (!metaBulletId) {
    console.debug('🚫 TextEntity не имеет metaBulletId');
    return false;
  }

  // Получаем все текстовые сущности текущего метабулита
  const entities = getMetaBulletEntities(canvas, metaBulletId);
  const sortedEntities = sortEntitiesByPosition(entities);

  // Находим индекс текущей сущности
  const currentIndex = sortedEntities.findIndex((entity) => entity.id === currentEntity.id);
  if (currentIndex === -1) {
    console.debug('🚫 Текущая сущность не найдена в списке');
    return false;
  }

  // Определяем направление: вверх/влево = -1, вниз/вправо = +1
  const delta = direction === 'up' || direction === 'left' ? -1 : 1;
  const targetIndex = currentIndex + delta;

  // Проверяем, вышли ли за пределы текущего метабулита
  if (targetIndex < 0) {
    // Пытаемся перейти к предыдущему метабулиту
    const allMetaBulletIds = getAllMetaBulletsIdsInOrder(canvas);
    const currentMetaBulletIndex = allMetaBulletIds.indexOf(metaBulletId);

    if (currentMetaBulletIndex > 0) {
      // Есть метабулит выше, переходим к его последней текстовой сущности
      const prevMetaBulletId = allMetaBulletIds[currentMetaBulletIndex - 1];
      const prevEntities = getMetaBulletEntities(canvas, prevMetaBulletId);
      const prevSortedEntities = sortEntitiesByPosition(prevEntities);
      const targetEntity = prevSortedEntities[prevSortedEntities.length - 1];

      if (targetEntity) {
        canvas.setActiveObject(targetEntity);
        canvas.requestRenderAll();
        return true;
      }
    }
    return false;
  } else if (targetIndex >= sortedEntities.length) {
    // Пытаемся перейти к следующему метабулиту
    const allMetaBulletIds = getAllMetaBulletsIdsInOrder(canvas);
    const currentMetaBulletIndex = allMetaBulletIds.indexOf(metaBulletId);

    if (currentMetaBulletIndex < allMetaBulletIds.length - 1) {
      // Есть метабулит ниже, переходим к его первой текстовой сущности
      const nextMetaBulletId = allMetaBulletIds[currentMetaBulletIndex + 1];
      const nextEntities = getMetaBulletEntities(canvas, nextMetaBulletId);
      const nextSortedEntities = sortEntitiesByPosition(nextEntities);
      const targetEntity = nextSortedEntities[0];

      if (targetEntity) {
        canvas.setActiveObject(targetEntity);
        canvas.requestRenderAll();
        return true;
      }
    }
    return false;
  }

  // Переключаемся на текстовую сущность в текущем метабулите
  const targetEntity = sortedEntities[targetIndex];
  if (targetEntity) {
    canvas.setActiveObject(targetEntity);
    canvas.requestRenderAll();
    return true;
  }

  return false;
};
